"use client";

import { useEffect, useRef, useState } from "react";
import {
  resolveDemoVideoPosterUrl,
  resolveDemoVideoUrl,
} from "@/lib/media/demo-video-url";

type DemoVideoAsset = {
  poster: string;
  src: string;
};

type WallColumn = {
  direction: "up" | "down";
  /** Phase offset (seconds) so same-direction columns don't move in lockstep. */
  delay: number;
  videos: DemoVideoAsset[];
};

function demoVideo(fileName: string): DemoVideoAsset {
  return {
    poster: resolveDemoVideoPosterUrl(fileName),
    src: resolveDemoVideoUrl(fileName),
  };
}

/**
 * Mix of existing + new example videos, distributed across three columns.
 * The outer columns drift downward, the middle column drifts up at the same
 * pace, showing off the variety of videos creators can make with ReelMagic.
 */
const columns: WallColumn[] = [
  {
    direction: "down",
    delay: 0,
    videos: [
      demoVideo("reddit-comments.mp4"),
      demoVideo("dark-comic.mp4"),
      demoVideo("history.mp4"),
      demoVideo("text-messages-example.mp4"),
      demoVideo("mary-celeste.mp4"),
      demoVideo("documentary.mp4"),
    ],
  },
  {
    direction: "up",
    delay: 0,
    videos: [
      demoVideo("pixar.mp4"),
      demoVideo("motivation.mp4"),
      demoVideo("reddit-comments-example.mp4"),
      demoVideo("mystery.mp4"),
    ],
  },
  {
    direction: "down",
    delay: -24,
    videos: [
      demoVideo("confession.mp4"),
      demoVideo("stickman.mp4"),
      demoVideo("underwater-mystery.mp4"),
      demoVideo("text-messages.mp4"),
      demoVideo("stickman-volcano-example.optimized.mp4"),
    ],
  },
];

/** Full-cycle duration shared by every column so the pace stays uniform. */
const SCROLL_DURATION_SECONDS = 48;

function VideoTile({ video }: { video: DemoVideoAsset }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        // Ignore autoplay restrictions (e.g. strict browser settings).
      });
    }
  }, []);

  return (
    <div className="relative mb-1.5 aspect-[9/16] w-full overflow-hidden rounded-[10px] border border-white/10 bg-black/30 shadow-[0_14px_38px_rgba(0,0,0,0.34)] sm:mb-2 sm:rounded-[12px] md:mb-2.5">
      <img
        alt=""
        aria-hidden="true"
        className={[
          "absolute inset-0 size-full object-cover transition-opacity duration-300",
          isVideoReady ? "opacity-0" : "opacity-100",
        ].join(" ")}
        decoding="async"
        src={video.poster}
      />
      <video
        ref={videoRef}
        className="absolute inset-0 size-full object-cover"
        src={video.src}
        autoPlay
        loop
        muted
        onLoadedData={() => setIsVideoReady(true)}
        onPlaying={() => setIsVideoReady(true)}
        playsInline
        preload="metadata"
      />
    </div>
  );
}

function WallColumnTrack({ column }: { column: WallColumn }) {
  const animationName =
    column.direction === "up" ? "heroWallScrollUp" : "heroWallScrollDown";

  // The track is rendered twice so a -50% translate lands seamlessly.
  const tiles = [...column.videos, ...column.videos];

  return (
    <div className="relative overflow-hidden">
      <div
        className="flex flex-col [will-change:transform] motion-reduce:!animate-none"
        style={{
          animation: `${animationName} ${SCROLL_DURATION_SECONDS}s linear ${column.delay}s infinite`,
        }}
      >
        {tiles.map((video, index) => (
          <VideoTile key={`${video.src}-${index}`} video={video} />
        ))}
      </div>
    </div>
  );
}

export function HeroVideoWall() {
  return (
    <div className="relative mx-auto w-full max-w-[min(100%,390px)] py-1 sm:max-w-[440px] sm:py-2 md:max-w-[500px] xl:mx-0 xl:max-w-[520px]">
      <div className="absolute inset-x-4 top-8 h-28 rounded-full bg-[radial-gradient(circle,rgba(217,70,239,0.24),rgba(168,85,247,0.08),transparent_72%)] blur-3xl sm:inset-x-8 sm:top-10 sm:h-36" />
      <div className="absolute inset-x-4 bottom-6 h-20 rounded-full bg-[radial-gradient(circle,rgba(217,70,239,0.3),rgba(168,85,247,0.12),transparent_72%)] blur-3xl sm:inset-x-8 sm:bottom-8 sm:h-24" />

      <div
        className={[
          "relative grid w-full grid-cols-3 gap-1.5 overflow-hidden sm:gap-2 md:gap-2.5",
          "h-[clamp(480px,68vh,580px)] sm:h-[500px] md:h-[560px] lg:h-[580px] xl:h-[660px]",
          "[mask-image:linear-gradient(180deg,transparent_0%,black_5%,black_92%,transparent_100%)]",
          "[-webkit-mask-image:linear-gradient(180deg,transparent_0%,black_5%,black_92%,transparent_100%)]",
          "sm:[mask-image:linear-gradient(180deg,transparent_0%,black_11%,black_89%,transparent_100%)]",
          "sm:[-webkit-mask-image:linear-gradient(180deg,transparent_0%,black_11%,black_89%,transparent_100%)]",
        ].join(" ")}
      >
        {columns.map((column, index) => (
          <WallColumnTrack key={index} column={column} />
        ))}
      </div>
    </div>
  );
}
